import { ChangeEvent, useEffect, useMemo, useState } from "react";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import { useToggle } from "../../hooks/useToggle";
import PageMeta from "../../components/common/PageMeta";
import { useSimplePagination } from "../../hooks/useSimplePagination";
import CommonTable, { type ColumnConfig } from "../../components/common/Table";
import {
  ALLOW_REFUND_USERS_COUNT,
  COMPLETION_STEP,
  PAGINATION_PER_PAGE,
} from "../../utils/constants";
import debounce from "lodash.debounce";
import Input from "../../components/form/input/InputField";
import { IUser } from "../../types/user";
import { exportUsers, usersList } from "../../services/user";
import Button from "../../components/ui/button/Button";
import { DollarLineIcon, DownloadIcon } from "../../icons";
import { useNavigate } from "react-router";

const columns: ColumnConfig<IUser>[] = [
  {
    key: "id",
    header: "ID",
    render(_, index) {
      return index + 1;
    },
  },
  {
    key: "name",
    header: "Name",
  },
  {
    key: "email",
    header: "Email",
    className: "max-w-64 truncate",
  },
  {
    key: "completionStep",
    header: "Completion Status",
    render(item) {
      return COMPLETION_STEP[item.completionStep] || item.completionStep;
    },
  },
  {
    key: "employmentStatus",
    header: "Employment Status",
    render(item) {
      return item.employmentStatus || "N/A";
    },
  },
  {
    key: "nationality",
    header: "Nationality",
    render(item) {
      return item.nationality || "N/A";
    },
  },
  {
    key: "gender",
    header: "Gender",
    render(item) {
      return item.gender || "N/A";
    },
  },
  {
    key: "referredByUser",
    header: "Eligible for Refund",
    render(item) {
      if (!item.referredByUser?.length) return "No";
      return item?.referredByUser?.length >= ALLOW_REFUND_USERS_COUNT
        ? "Yes"
        : "No";
    },
  },
];

const Users = () => {
  const [users, setUsers] = useState<IUser[]>([]);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const { state: isLoading, on: showLoader, off: hideLoader } = useToggle();

  const {
    state: isExporting,
    on: startExport,
    off: completeExport,
  } = useToggle();

  const { currentPage, totalPages, onPageChange, setTotalPages } =
    useSimplePagination();

  const fetchEvents = async () => {
    if (isLoading) return;
    showLoader();
    const res = await usersList({
      params: {
        query,
        page: currentPage,
        limit: PAGINATION_PER_PAGE,
      },
    }).finally(() => {
      hideLoader();
    });
    setUsers(res.rows);
    setTotalPages(res.count);
  };

  const debouncedResults = useMemo(() => {
    return debounce(fetchEvents, 500);
    // eslint-disable-next-line
  }, [query, currentPage]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  const exportAsCSV = async () => {
    startExport();
    if (isExporting) return;
    const csv = await exportUsers();
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const date = new Date().toLocaleString();
    link.setAttribute("download", `users_${date}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    completeExport();
  };

  useEffect(() => {
    debouncedResults();
    return () => {
      debouncedResults.cancel();
    };
  }, [debouncedResults]);

  return (
    <div>
      <PageMeta title="Users" description="List of users" />
      <PageBreadcrumb pageTitle="Users" />
      <div className="flex justify-end items-center my-5 gap-5">
        <Input
          placeholder="Search by Name, Email or education"
          value={query}
          onChange={handleChange}
          wrapperClass="w-full"
        />
        <Button
          size="sm"
          startIcon={<DownloadIcon />}
          onClick={exportAsCSV}
          disabled={isExporting}
        >
          Export
        </Button>
      </div>
      <CommonTable
        columns={columns}
        data={users}
        isLoading={isLoading}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={onPageChange}
        actions={[
          {
            label: <DollarLineIcon className="text-blue-500" />,
            onClick(item) {
              navigate(`/users/${item.id}/referrals`);
            },
            size: "sm",
            title: "View Referrals",
          },
        ]}
        showPagination
      />
    </div>
  );
};

export default Users;
